/* eslint-disable no-case-declarations */
import { PromisePool } from "@supercharge/promise-pool";
import { ArweaveSigner } from "arbundles";
import base64url from "base64url";
import { ChunkingUploader } from "./chunkingUploader.js";
import { UploadHeaders, } from "./types.js";
import { randomBytes } from "crypto";
import retry from "async-retry";
export const sleep = (ms) => new Promise((resolve) => setTimeout(resolve, ms));
export const CHUNKING_THRESHOLD = 50_000_000;
// eslint-disable-next-line @typescript-eslint/naming-convention
export class Uploader {
    api;
    token;
    tokenConfig;
    utils;
    contentTypeOverride;
    forceUseChunking;
    arbundles;
    irysTransaction;
    constructor(api, utils, token, tokenConfig, irysTransaction) {
        this.api = api;
        this.token = token;
        this.tokenConfig = tokenConfig;
        this.arbundles = this.tokenConfig.irys.arbundles;
        this.utils = utils;
        this.irysTransaction = irysTransaction;
    }
    async uploadTransaction(transaction, opts) {
        let res;
        const isDataItem = this.arbundles.DataItem.isDataItem(transaction);
        if (this.forceUseChunking || (isDataItem && transaction.getRaw().length >= CHUNKING_THRESHOLD) || !isDataItem) {
            res = await this.chunkedUploader.uploadTransaction(isDataItem ? transaction.getRaw() : transaction, opts);
        }
        else {
            const { url, timeout, headers: confHeaders } = this.api.getConfig();
            const headers = { "Content-Type": "application/octet-stream", ...confHeaders };
            if (opts?.paidBy)
                headers[UploadHeaders.PAID_BY] = opts.paidBy;
            res = await this.api.post(new URL(`/tx/${this.token}`, url).toString(), transaction.getRaw(), {
                headers: headers,
                timeout,
                maxBodyLength: Infinity,
            });
            if (res.status === 201) {
                throw new Error(res.data);
            }
        }
        switch (res.status) {
            case 402:
                const retryAfterHeader = res?.headers?.["retry-after"];
                const errorMsg = res.data + (retryAfterHeader ? ` - retry after ${retryAfterHeader}s` : "");
                throw new Error(errorMsg);
            default:
                if (res.status >= 400) {
                    throw new Error(`whilst uploading Irys transaction: ${res.status} ${res.statusText}`);
                }
        }
        res.data.verify = async () => this.utils.verifyReceipt(res.data);
        return res;
    }
    async uploadData(data, opts) {
        if (typeof data === "string") {
            data = Buffer.from(data);
        }
        if (Buffer.isBuffer(data)) {
            if (data.length <= CHUNKING_THRESHOLD) {
                const dataItem = this.arbundles.createData(data, this.tokenConfig.getSigner(), {
                    ...opts,
                    anchor: opts?.anchor ?? randomBytes(32).toString("base64").slice(0, 32),
                });
                await dataItem.sign(this.tokenConfig.getSigner());
                return (await this.uploadTransaction(dataItem, { ...opts?.upload })).data;
            }
        }
        return (await this.chunkedUploader.uploadData(data, opts)).data;
    }
    // concurrently uploads transactions
    async concurrentUploader(data, opts) {
        const errors = [];
        const logFn = opts?.logFunction
            ? opts?.logFunction
            : async (_) => {
                return;
            };
        const concurrency = opts?.concurrency ?? 5;
        const results = (await PromisePool.for(data)
            .withConcurrency(concurrency >= 1 ? concurrency : 5)
            .handleError(async (error, _) => {
            errors.push(error);
            if (error.message === "Not enough funds to send data") {
                throw error;
            }
        })
            .process(async (item, i, _) => {
            await retry(async (bail) => {
                try {
                    const res = await this.processItem(item, opts?.itemOptions);
                    if (i % concurrency == 0) {
                        await logFn(`Processed ${i} Items`);
                    }
                    if (opts?.resultProcessor) {
                        return await opts.resultProcessor({ item, res, i });
                    }
                    else {
                        return { item, res, i };
                    }
                }
                catch (e) {
                    if (e?.message === "Not enough funds to send data") {
                        bail(e);
                    }
                    throw e;
                }
            }, { retries: 3, minTimeout: 1000, maxTimeout: 10_000 });
        }));
        return { errors, results: results.results };
    }
    async processItem(data, opts) {
        if (this.arbundles.DataItem.isDataItem(data)) {
            return this.uploadTransaction(data, { ...opts?.upload });
        }
        return this.uploadData(data, opts);
    }
    /**
     * geneates a manifest JSON object
     * @param config.items mapping of logical paths to item IDs
     * @param config.indexFile optional logical path of the index file for the manifest
     * @returns
     */
    async generateManifest(config) {
        const { items, indexFile } = config;
        const manifest = {
            manifest: "arweave/paths",
            version: "0.1.0",
            paths: {},
        };
        if (indexFile) {
            if (!items.has(indexFile)) {
                throw new Error(`Unable to access item: ${indexFile}`);
            }
            manifest.index = { path: indexFile };
        }
        for (const [k, v] of items.entries()) {
            // @ts-expect-error constant index type
            manifest.paths[k] = { id: v };
        }
        return manifest;
    }
    get chunkedUploader() {
        return new ChunkingUploader(this.tokenConfig, this.api);
    }
    set useChunking(state) {
        if (typeof state === "boolean") {
            this.forceUseChunking = state;
        }
    }
    set contentType(type) {
        // const fullType = mime.contentType(type)
        // if(!fullType){
        //     throw new Error("Invali")
        // }
        this.contentTypeOverride = type;
    }
    async uploadBundle(transactions, opts) {
        const throwawayKey = opts?.throwawayKey ?? (await this.arbundles.getCryptoDriver().generateJWK());
        const ephemeralSigner = new ArweaveSigner(throwawayKey);
        const txs = transactions.map((tx) => (this.arbundles.DataItem.isDataItem(tx) ? tx : this.arbundles.createData(tx, ephemeralSigner)));
        const bundle = await this.arbundles.bundleAndSignData(txs, ephemeralSigner);
        // upload bundle with bundle specific tags, use actual signer for this.
        const tx = this.arbundles.createData(bundle.getRaw(), this.tokenConfig.getSigner(), {
            tags: [
                { name: "Bundle-Format", value: "binary" },
                { name: "Bundle-Version", value: "2.0.0" },
            ],
        });
        await tx.sign(this.tokenConfig.getSigner());
        const res = await this.uploadTransaction(tx, opts);
        const throwawayKeyAddress = base64url(Buffer.from(await this.arbundles.getCryptoDriver().hash(base64url.toBuffer(base64url(ephemeralSigner.publicKey)))));
        return { ...res, txs, throwawayKey, throwawayKeyAddress };
    }
}
export default Uploader;
//# sourceMappingURL=upload.js.map